import React, { createContext, ReactNode, useContext, useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import { apiService } from '../services/api';
import { User, UserDetailed } from '../types/api';

interface AuthContextType {
  user: User | null;
  userDetailed: UserDetailed | null;
  isLoading: boolean;
  isAuthenticated: boolean;
  login: (email: string, password: string) => Promise<void>;
  loginWithGoogle: (credential: string) => Promise<void>;
  loginWithFacebook: (params: { accessToken: string; providerId: string; email?: string; name?: string }) => Promise<void>;
  register: (fullName: string, email: string, password: string) => Promise<void>;
  logout: () => void;
  refreshUser: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [userDetailed, setUserDetailed] = useState<UserDetailed | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  const isAuthenticated = !!user;

  useEffect(() => {
    const initAuth = async () => {
      const token = localStorage.getItem('authToken');
      const storedUser = localStorage.getItem('user');
      
      if (token && storedUser) {
        try {
          setUser(JSON.parse(storedUser));
          // Optionally refresh user data
          await refreshUser();
        } catch (error) {
          console.error('Error initializing auth:', error);
          localStorage.removeItem('authToken');
          localStorage.removeItem('user');
        }
      }
      setIsLoading(false);
    };

    initAuth();
  }, []);

  const login = async (email: string, password: string) => {
    try {
      const response = await apiService.login({ email, password });
      const { user: userData, token } = response;
      
      localStorage.setItem('authToken', token.value);
      localStorage.setItem('user', JSON.stringify(userData));
      setUser(userData);
      
    } catch (error: any) {
      const errorMessage = error.response?.data?.message || 'Login failed';
      toast.error(errorMessage);
      throw error;
    }
  };

  const loginWithGoogle = async (credential: string) => {
    try {
      const response = await apiService.googleAuth(credential);
      const { user: userData, token } = response;

      localStorage.setItem('authToken', token.value);
      localStorage.setItem('user', JSON.stringify(userData));
      setUser(userData);
    } catch (error: any) {
      const errorMessage = error.response?.data?.message || 'Sign in with Google failed';
      toast.error(errorMessage);
      throw error;
    }
  };

  const loginWithFacebook = async (params: { accessToken: string; providerId: string; email?: string; name?: string }) => {
    try {
      const response = await apiService.socialLogin({
        provider: 'facebook',
        providerId: params.providerId,
        accessToken: params.accessToken,
        email: params.email,
        name: params.name,
      });
      const { user: userData, token } = response;

      localStorage.setItem('authToken', token.value);
      localStorage.setItem('user', JSON.stringify(userData));
      setUser(userData);
    } catch (error: any) {
      const errorMessage = error.response?.data?.message || 'Sign in with Facebook failed';
      toast.error(errorMessage);
      throw error;
    }
  };

  const register = async (fullName: string, email: string, password: string) => {
    try {
      const response = await apiService.register({ fullName, email, password });
      const { user: userData, token } = response;
      
      localStorage.setItem('authToken', token.value);
      localStorage.setItem('user', JSON.stringify(userData));
      setUser(userData);
      
      toast.success('Registration successful!');
    } catch (error: any) {
      const errorMessage = error.response?.data?.message || 'Registration failed';
      toast.error(errorMessage);
      throw error;
    }
  };

  const logout = () => {
    localStorage.removeItem('authToken');
    localStorage.removeItem('user');
    setUser(null);
    setUserDetailed(null);
    toast.success('Logged out successfully');
  };

  const refreshUser = async () => {
    try {
      const response = await apiService.getCurrentUser();
      setUserDetailed(response.user);
    } catch (error) {
      console.error('Error refreshing user data:', error);
    }
  };

  const value: AuthContextType = {
    user,
    userDetailed,
    isLoading,
    isAuthenticated,
    login,
    loginWithGoogle,
    loginWithFacebook,
    register,
    logout,
    refreshUser,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};
